/**
 * Widget 外观配置工具函数
 *
 * 提供默认值常量和从 widget.options 解析外观配置的工具函数，
 * 兼容 legacy hideTitle / hideTimestamp 字段。
 *
 * Validates: Requirements 5.5, 5.6, 11.2, 11.4, 11.5
 */

/**
 * Widget 外观配置的默认值
 * @type {AppearanceConfig}
 */
export const DEFAULT_APPEARANCE = {
  show_header: true,
  show_refresh_time: true,
  border: { show: false, width: 1, color: "#d9d9d9" },
  border_radius: 0,
  opacity: 1.0,
  glassmorphism: false,
  blur_intensity: 10,
};

/**
 * 从 widget.options 解析出完整的 AppearanceConfig。
 *
 * Legacy 兼容规则：
 * - 若 appearance.show_header 未定义，回退到 widgetOptions.hideTitle ? false : true
 * - 若 appearance.show_refresh_time 未定义，回退到 widgetOptions.hideTimestamp ? false : true
 * - 其余字段使用 ?? DEFAULT_APPEARANCE.xxx 回退
 *
 * @param {object} widgetOptions - widget.options 对象，默认为空对象
 * @returns {AppearanceConfig} 包含所有字段的完整外观配置对象
 */
export function resolveAppearance(widgetOptions = {}) {
  const appearance = widgetOptions.appearance || {};

  return {
    // Legacy 兼容：show_header 回退到 !hideTitle
    show_header:
      appearance.show_header !== undefined
        ? appearance.show_header
        : (widgetOptions.hideTitle ? false : DEFAULT_APPEARANCE.show_header),

    // Legacy 兼容：show_refresh_time 回退到 !hideTimestamp
    show_refresh_time:
      appearance.show_refresh_time !== undefined
        ? appearance.show_refresh_time
        : (widgetOptions.hideTimestamp ? false : DEFAULT_APPEARANCE.show_refresh_time),

    // border 对象：合并默认值与用户配置
    border: { ...DEFAULT_APPEARANCE.border, ...(appearance.border || {}) },

    border_radius: appearance.border_radius ?? DEFAULT_APPEARANCE.border_radius,
    opacity: appearance.opacity ?? DEFAULT_APPEARANCE.opacity,
    glassmorphism: appearance.glassmorphism ?? DEFAULT_APPEARANCE.glassmorphism,
    blur_intensity: appearance.blur_intensity ?? DEFAULT_APPEARANCE.blur_intensity,
  };
}

/**
 * 将 AppearanceConfig 转换为应用于 .tile.body-container 的 inline style 对象。
 *
 * @param {Partial<AppearanceConfig>} appearance - 外观配置（可为部分字段）
 * @returns {object} React inline style 对象
 */
export function buildTileStyle(appearance) {
  const cfg = { ...DEFAULT_APPEARANCE, ...appearance, border: { ...DEFAULT_APPEARANCE.border, ...(appearance && appearance.border) } };
  const style = {};

  // 背景色（支持透明穿透至 Dashboard 背景图）
  style.backgroundColor = `rgba(255, 255, 255, ${cfg.opacity})`;

  // 圆角
  if (cfg.border_radius > 0) {
    style.borderRadius = `${cfg.border_radius}px`;
  }

  // 边框
  if (cfg.border.show && cfg.border.width > 0) {
    style.border = `${cfg.border.width}px solid ${cfg.border.color}`;
  } else {
    style.border = "none";
    style.boxShadow = "none";
  }

  // 毛玻璃效果（同时设置标准属性和 -webkit- 前缀）
  if (cfg.glassmorphism) {
    const blur = `blur(${cfg.blur_intensity}px)`;
    style.backdropFilter = blur;
    style.WebkitBackdropFilter = blur;
  }

  return style;
}
